<script setup lang="ts">
import BannerAnnouncer from '@/components/banners/BannerAnnouncer.vue'
import { focusFirstChild, setFocusTrap } from '@/composables/useFocusTrap'
import { useConfirmStore } from '@/store/confirm'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Does clicking outside the modal cancel? */
    outsideClickCancels?: boolean
    /** Does pressing the esc key cancel? */
    escapeCancels?: boolean
    /** What header should be shown in  confirmation modal when the user cancels? */
    confirmCancelHeading?: string
    /** What confirmation message to show when the user cancels (no message if empty)? */
    confirmCancel?: string
    /** What is the id of the element containing the modal's label (So the modal gets an accessible name)? */
    labelId: string
    /** What is the id of the element containing the modal's description (So the modal gets an accessible description)? */
    descriptionId?: string
    /** How large should the modal be? */
    widthVariant?: 'sm' | 'md' | 'lg'
    /** Is thia a full screen modal? */
    fullScreen?: boolean
    /** How much margin (in pixels) should be added to the top of the modal? */
    marginTop?: number
  }>(),
  {
    outsideClickCancels: true,
    escapeCancels: true,
    widthVariant: 'sm',
    fullScreen: false,
    marginTop: 0
  }
)

const emit = defineEmits<{
  (e: 'cancel'): void
}>()

const element = ref<HTMLDivElement | undefined>()
const confirm = useConfirmStore()
let lastFocusedElement

const handleKeydown = (e) => {
  if (props.escapeCancels && e.key == 'Escape') {
    handleCancelModal()
  } else if (setFocusTrap(e, [element.value])) {
    // focus trap handled the event
  }
}

const handleCloseModal = () => {
  if (props.outsideClickCancels) {
    handleCancelModal()
  }
}

const handleCancelModal = () => {
  // Passing in an empty string for the heading will not show the heading because it's falsey
  if (props.confirmCancel) {
    confirm.open(
      'uiModalConfirmCancel',
      props.confirmCancelHeading ? props.confirmCancelHeading : '',
      props.confirmCancel,
      'discard',
      'destructive'
    )
  } else {
    emit('cancel')
  }
}

const handleConfirmAction = ({ name, store }) => {
  if (name == 'confirm') {
    if (store.key == 'uiModalConfirmCancel') {
      emit('cancel')
    }
  }
}

const computedMarginVars = computed(() => ({
  '--mt-sm': `${10 + props.marginTop}px`,
  '--mt-md': `${20 + props.marginTop}px`,
  '--mt-2xl': `${28 + props.marginTop}px`
}))

const computedMaxWidthVar = computed(() => {
  const width =
    props.widthVariant == 'lg' ? 888 : props.widthVariant == 'md' ? 610 : 532
  return { '--maxw': `${width}px` }
})

confirm.$onAction(handleConfirmAction)

onMounted(() => {
  lastFocusedElement = document.activeElement
  focusFirstChild(element.value)
})

onBeforeUnmount(() => {
  lastFocusedElement?.focus()
})

// should this modal be hidden because a confirm modal is showing?
const hideBecauseConfirmModalShowing = computed(() => {
  if (props.confirmCancel) {
    return confirm.isOpen
  } else {
    return false
  }
})
</script>

<template>
  <div
    ref="element"
    v-global-keydown="handleKeydown"
    :class="{ hidden: hideBecauseConfirmModalShowing }"
    class="ui-modal fixed inset-0 z-50 flex items-center sm:items-start justify-center"
  >
    <button
      class="fixed w-full h-full bg-black opacity-20"
      tabindex="-1"
      aria-hidden="true"
      @click="handleCloseModal"
    ></button>
    <div
      :class="[
        {
          hidden: hideBecauseConfirmModalShowing,
          fixed: !hideBecauseConfirmModalShowing,
          'sm:block': !hideBecauseConfirmModalShowing,
          'left-[10px] right-[10px]': !props.fullScreen,
          'left-0 right-0 top-0 bottom-0': props.fullScreen
        }
      ]"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="props.labelId"
      :aria-describedby="props.descriptionId"
    >
      <BannerAnnouncer />
      <div
        :class="[
          {
            'w-full h-full m-0 rounded-none sm:rounded-none sm:m-0':
              props.fullScreen,
            'sm:w-[var(--maxw)] sm:mx-auto rounded-[15px] sm:mt-[var(--mt-sm)] md:mt-[var(--mt-md)] 2xl:mt-[var(--mt-2xl)]':
              !props.fullScreen,
            'sm:top-20 sm:bottom-20 relative':
              props.widthVariant === 'lg' && !props.fullScreen,
            'max-h-[calc(100%-20px)] sm:mt-[10px] md:mt-5 2xl:mt-[112px] overflow-y-auto':
              props.widthVariant !== 'lg' && !props.fullScreen
          }
        ]"
        :style="{ ...computedMarginVars, ...computedMaxWidthVar }"
        class="shadow z-50 bg-elevated relative"
      >
        <slot :on-cancel-clicked="handleCloseModal"></slot>
      </div>
      <div
        v-if="!props.fullScreen"
        :class="[
          'sm:w-[var(--maxw)]',
          {
            'sm:top-20 sm:bottom-20 max-h-[calc(100%-20px)] overflow-y-auto':
              props.widthVariant == 'lg'
          }
        ]"
        class="shadow z-50 bg-elevated sm:mx-auto rounded-[15px] mt-4"
        :style="computedMaxWidthVar"
      >
        <slot name="sub-modal" :on-cancel-clicked="handleCloseModal"></slot>
      </div>
      <div
        v-if="!props.fullScreen && $slots['sub-modal-secondary']"
        class="sm:w-[var(--maxw)] shadow z-50 bg-elevated sm:mx-auto rounded-[15px] mt-4"
        :style="computedMaxWidthVar"
      >
        <slot
          name="sub-modal-secondary"
          :on-cancel-clicked="handleCloseModal"
        ></slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ui-modal {
  animation: fadein 0.25s;
}

@keyframes fadein {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
